import { uploadImageToS3 } from '@/utils/imageUtils';
import { v4 as uuidv4 } from 'uuid';
import { Mutex } from './mutex';
import { cloneDeep } from 'lodash';
import {
    createCounterpartGroupSync,
    collectEpsElements,
    collectPreviewElements,
    createGroupIndex,
    normalizeElementGroupAssignments,
    normalizeGroupParentReferences,
    resolveGroupIdForTypeWithCounterpart,
    removeGroupsCollectionByType,
    replaceGroupsCollectionByType,
    syncPreviewGroupsFromEps
} from './groups/groupSyncUtils';

(function () {
    'ngInject';

    angular.module('app.shared.services').factory('ProductService', productService);

    function productService(ProductAPIService, ImageUtilService, GeneralMathUtils, ElementsColorService, $q) {
        // Default structure
        const defaultPreview = {
            PreviewId: 0,
            name: 'Default',
            isDefault: true,
            id: 1,
            zIndex: 1,
            imagePath: '',
            bgColor: '',
            width: 0,
            height: 0,
            textsPreview: [],
            circularTextsPreview: [],
            imagePlaceHoldersPreview: [],
            vectorEPSPreview: [],
            textTextures: []
        };
        let productStructure = {
            id: '',
            name: '',
            referenceId: '',
            format: '',
            colorMode: '',
            MergeFiles: false,
            preview: undefined,
            listEPS: [],
            listPreviews: [
                {
                    ...defaultPreview
                }
            ],
            groups: [],
            elementsLinks: [],
            willUpdateOptionSets: false
        };
        var product = JSON.parse(JSON.stringify(productStructure));
        var needsColorUpgrade = false;
        var productTemplatePreviewTargetSize = null;
        var podAssignments = null;
        var selectedPod = null;
        var templateProductVariants = null;
        var childTemplateProduct = null;
        var previewColorOptionsMetadata = null;
        var productColorsFilename = [];
        var productDescription = '';
        var isProductColorModified = false;
        var starMapMutex = new Mutex();
        var qrMutex = new Mutex();
        const previewGroupSync = createCounterpartGroupSync(() => getGroups(), 'eps', 'preview');

        console.log('StarMap mutex was created');

        function getStarMapMutex() {
            return starMapMutex;
        }

        function getQrMutex() {
            return qrMutex;
        }

        let isProductEditMode = true;
        /**
         * Synchronizes Preview group state from the current EPS state.
         *
         * EPS is treated as the source of truth for the group structure. This method keeps Preview aligned by:
         * - removing Preview groups whose EPS counterpart no longer exists,
         * - creating missing Preview groups for new EPS groups,
         * - updating Preview hierarchy and metadata from EPS groups,
         * - and reassigning Preview element groupId values using shared element uuid.
         *
         * EPS and Preview groups are matched by shared uuid. Their ids are expected to differ.
         */
        function updatePreviewGroups() {
            const listEps = getListEPS();
            const syncResult = syncPreviewGroupsFromEps(product.groups, listEps, getListPreview(), () => uuidv4());
            product.groups = syncResult.groups;
        }

        function getGroups() {
            return product.groups;
        }

        function getElementsLinks() {
            return product.elementsLinks || [];
        }

        function setElementsLinks(elementsLinks) {
            product.elementsLinks = elementsLinks || [];
        }

        function addGroups(groups) {
            product.groups.push(...groups);
        }

        function replaceGroupsByType(type, groups = []) {
            const sourceGroups = groups || [];
            const incoming = sourceGroups.filter((group) => group?.type === type);
            product.groups = replaceGroupsCollectionByType(product.groups, type, incoming);
        }

        /**
         * Gets the eps by id.
         * If the id is null, returns the first eps in the list.
         * @param {Number}  epsId eps id (by default null)
         */
        function getEps(epsId = null) {
            if (epsId) {
                return product.listEPS.find((x) => x.id == epsId);
            }

            return (product.listEPS && product.listEPS[0]) || product.eps;
        }

        function getElementsIdAndTestingValues() {
            try {
                const listEps = getEps();
                const elements = [];
                const uuids = [];
                if (product.listEPS.length >= 2) {
                    for (const eps of product.listEPS) {
                        elements.push(...eps.textsEps);
                        elements.push(...eps.circularTextsEps);
                        elements.push(...eps.imagePlaceHoldersEps);
                        elements.push(...eps.vectorEPSEps);
                    }
                } else if (listEps) {
                    elements.push(...listEps.textsEps);
                    elements.push(...listEps.circularTextsEps);
                    elements.push(...listEps.imagePlaceHoldersEps);
                    elements.push(...listEps.vectorEPSEps);
                }
                elements?.forEach((element) => {
                    uuids.push({
                        uuid: element.uuid,
                        value: element.testingText || element.testingUrl || element.svgString
                    });
                });
                return uuids;
            } catch (error) {
                console.error(error);
            }
        }

        function applyTestingValues(elementArray, prop, valueMap, needImageRender, canvas) {
            if (!elementArray) return;
            for (const element of elementArray) {
                if (!valueMap[element.uuid]) {
                    continue;
                }
                if (needImageRender) {
                    if (!canvas) continue;
                    const object = canvas
                        .getObjects()
                        .find((o) => o.type === 'image-placeholder' && o.uuid === element.uuid);
                    if (!object?.visible) continue;
                    const imgAux = document.createElement('img');
                    imgAux.onload = function () {
                        object.setElement(imgAux);
                        canvas.renderAll();
                    };
                    imgAux.src = valueMap[element.uuid];
                    object.testingUrl = imgAux.src;
                }
                element[prop] = valueMap[element.uuid];
            }
        }

        function setTestingValues(values) {
            const valueMap = Object.fromEntries(values.map((item) => [item.elementId, item.value]));

            for (const obj of product.listEPS) {
                applyTestingValues(obj.textsEps, 'testingText', valueMap);
                applyTestingValues(obj.circularTextsEps, 'testingText', valueMap);
                applyTestingValues(obj.vectorEPSEps, 'svgString', valueMap);
            }

            if (product.preview) {
                applyTestingValues(product.preview.textsPreview, 'testingText', valueMap);
                applyTestingValues(product.preview.circularTextsPreview, 'testingText', valueMap);
                applyTestingValues(product.preview.vectorEPSPreview, 'svgString', valueMap);
            }
        }

        function setImagesTestingValues(values, canvas) {
            const valueMap = Object.fromEntries(values.map((item) => [item.elementId, item.value]));
            for (const obj of product.listEPS) {
                applyTestingValues(obj.imagePlaceHoldersEps, 'testingUrl', valueMap, true, canvas);
            }
            if (product.preview) {
                applyTestingValues(product.preview.imagePlaceHoldersPreview, 'testingUrl', valueMap, true, canvas);
            }
        }

        function getListEPS() {
            return product.listEPS;
        }

        function AssingObj(source = {}, target = {}) {
            const propStrict = Object.keys(target);
            return Object.keys(source).reduce((result, prop) => {
                if (propStrict.includes(prop)) {
                    result[prop] = cloneDeep(source[prop]);
                }
                return result;
            }, cloneDeep(target));
        }

        /**
         * Adds a new eps with default values.
         */
        function addNewEPS(eps = {}, singleEps = false) {
            const epsElement = AssingObj(eps, getDefaultEps());
            epsElement.zIndexEps = eps.zIndex;
            if (!singleEps) epsElement.zIndexPreview = null;
            product.listEPS.push(epsElement);
        }

        function addListEPS(listEps = []) {
            for (const eps of listEps) {
                const epsElement = AssingObj(eps, getDefaultEps());
                epsElement.zIndexEps = eps.zIndex;
                if (epsElement.zIndexPreview == null) epsElement.zIndexPreview = eps.zIndex;
                epsElement.name = eps.name;
                product.listEPS.push(epsElement);
            }
        }

        function updateEPSFromPreview(listEps = []) {
            for (const eps of listEps) {
                if (eps.type !== 'preview') continue;
                const elementEps = getEps(eps.id);
                elementEps.zIndexPreview = eps.zIndex;
            }
        }

        function deleteEPS(epsId) {
            product.listEPS = product.listEPS.filter((x) => x.id !== epsId);
        }

        /**
         * Returns an eps with default values.
         */
        function getDefaultEps() {
            return {
                id: 1,
                width: 0,
                height: 0,
                dpi: 300,
                exportPPI: 0,
                mirror: false,
                mergeOutput: false,
                baseImage: '',
                baseEps: '',
                textsEps: [],
                circularTextsEps: [],
                imagePlaceHoldersEps: [],
                vectorEPSEps: [],
                name: 'Page',
                zIndexEps: 0,
                zIndexPreview: 0,
                podPlaceholder: null,
                measureUnit: 'mm'
            };
        }

        function setPreviewColorOptionsMetadata(paths) {
            previewColorOptionsMetadata = paths;
        }

        function getPreviewColorOptionsMetadata() {
            return previewColorOptionsMetadata;
        }

        function setPodAssignments(assignments) {
            podAssignments = assignments;
        }

        function getPodAssignments() {
            return podAssignments;
        }

        function getPod() {
            return selectedPod;
        }

        function setPod(pod) {
            selectedPod = pod;
        }

        function setIsProductColorModified(productColorModified) {
            isProductColorModified = productColorModified;
        }
        function getIsProductColorModified() {
            return isProductColorModified;
        }
        function getProductColorsFilenames() {
            return productColorsFilename;
        }

        function setDescription(description) {
            productDescription = description;
        }

        function getDescription() {
            return productDescription;
        }
        function setProductColorsFilenames(filenamesArray) {
            const newFiles = filenamesArray.filter((f) => f.optionId > productColorsFilename.length);

            if (newFiles.length) {
                productColorsFilename = [...productColorsFilename, ...newFiles];
            }
            if (productColorsFilename.length) {
                productColorsFilename.map((file) => {
                    const updatedFilename = filenamesArray.find((f) => f.optionId == file.optionId);
                    if (updatedFilename) file.name = updatedFilename.name;
                });
            }
        }
        function setTemplateProductVariants(assignments) {
            templateProductVariants = assignments;
        }

        function getTemplateProductVariants() {
            return templateProductVariants;
        }

        function setChildTemplateProduct(templateProduct) {
            childTemplateProduct = templateProduct;
        }

        function getChildTemplateProduct() {
            return childTemplateProduct;
        }

        function getProductWillUpdateOptionSets() {
            return product.willUpdateOptionSets;
        }

        function setProductWillUpdateOptionSets(value) {
            product.willUpdateOptionSets = value;
        }

        function clearChildTemplateProduct() {
            childTemplateProduct = {
                parentId: null,
                forChildTemplateProductCreation: null,
                oldPrintVariantName: null,
                newPrintVariantName: null,
                currentTemplate: {
                    oldVariant: null,
                    newVariant: null,
                    dimension: {
                        width: null,
                        height: null,
                        unit: null
                    }
                },
                savedTemplates: [],
                isCompleted: null
            };
        }

        function setProductTemplatePreviewTargetSize(width, height) {
            productTemplatePreviewTargetSize = {
                width: width,
                height: height
            };
        }

        function getProductTemplatePreviewTargetSize() {
            return productTemplatePreviewTargetSize;
        }

        function loadProduct(p) {
            clearProduct();
            product = p;
            product.listEPS = p.listEPS || [p.eps];
            if (p.preview) {
                // Set default name and id before migrating from old preview structure
                p.preview.PreviewId = 0;
                p.preview.name = 'Default';
                p.preview.isDefault = true;
                p.preview.id = 1;
                p.preview.zIndex = 1;
            }
            product.listPreviews = p.listPreviews?.length ? p.listPreviews : [p.preview];

            // Handle migration from old preview structure where listPreviews had empty name and only one preview existed
            if (product.listPreviews?.length === 1 && product.listPreviews[0].name === null) {
                const listPreview = product.listPreviews[0];
                listPreview.PreviewId = 0;
                listPreview.name = 'Default';
                listPreview.isDefault = true;
                listPreview.id = 1;
                listPreview.zIndex = 1;
            }

            product.preview = undefined;

            const groups = product.groups || [];
            for (const group of groups) {
                group.isLayout = group.isLayout ?? true;
            }

            // Ensure all element arrays are initialized
            product.listEPS.forEach((eps) => {
                eps.textsEps = eps.textsEps || [];
                eps.circularTextsEps = eps.circularTextsEps || [];
                eps.imagePlaceHoldersEps = eps.imagePlaceHoldersEps || [];
                eps.vectorEPSEps = eps.vectorEPSEps || [];
            });

            product.listPreviews.forEach((preview) => {
                preview.textsPreview = preview.textsPreview || [];
                preview.circularTextsPreview = preview.circularTextsPreview || [];
                preview.imagePlaceHoldersPreview = preview.imagePlaceHoldersPreview || [];
                preview.vectorEPSPreview = preview.vectorEPSPreview || [];
            });

            const groupIndex = createGroupIndex(groups);

            // Normalize group hierarchy references before any step-specific synchronization runs.
            normalizeGroupParentReferences(groups, groupIndex.byId, groupIndex.byTypeAndUuid);

            // Normalize EPS element assignments.
            product.listEPS.forEach((eps) => {
                normalizeElementGroupAssignments(
                    collectEpsElements([eps]),
                    groupIndex.byId,
                    'eps',
                    groupIndex.byTypeAndUuid
                );
            });

            // Normalize Preview element assignments.
            product.listPreviews.forEach((preview) => {
                normalizeElementGroupAssignments(
                    collectPreviewElements([preview]),
                    groupIndex.byId,
                    'preview',
                    groupIndex.byTypeAndUuid
                );
            });
        }

        function clearProduct() {
            product = JSON.parse(JSON.stringify(productStructure));
            needsColorUpgrade = false;
            productTemplatePreviewTargetSize = null;
            podAssignments = null;
            previewColorOptionsMetadata = null;
            selectedPod = null;
        }

        function clearEPS() {
            if (typeof product.eps !== 'undefined') delete product.eps;
            product.listEPS = JSON.parse(JSON.stringify(productStructure)).listEPS;
            product.groups = removeGroupsCollectionByType(product.groups, 'eps');
        }

        function clearPreview() {
            const preview = getPreview();
            const newPreview = JSON.parse(JSON.stringify(defaultPreview));
            newPreview.name = preview.name;
            newPreview.PreviewId = preview.PreviewId;
            newPreview.isDefault = preview.isDefault;
            newPreview.id = preview.id;
            newPreview.zIndex = preview.zIndex;
            Object.assign(preview, newPreview);
            product.groups = removeGroupsCollectionByType(product.groups, 'preview');
            productTemplatePreviewTargetSize = null;
            previewColorOptionsMetadata = null;
        }

        function clearPreviewElements() {
            const listPreview = getListPreview();

            for (const preview of listPreview) {
                if (!preview) return;

                Object.assign(preview, {
                    textsPreview: [],
                    circularTextsPreview: [],
                    imagePlaceHoldersPreview: [],
                    vectorEPSPreview: [],
                    textTextures: []
                });
            }

            product.groups = removeGroupsCollectionByType(product.groups, 'preview');
        }

        function setProductId(id) {
            product.id = id;
        }

        function getProductId() {
            return product.id;
        }

        function setProductName(name) {
            product.name = name;
        }

        function setMelcoReferenceId(referenceId) {
            product.referenceId = referenceId;
        }

        function setProductFormat(format) {
            product.format = format;
        }

        function setProductColorMode(model) {
            product.colorMode = model;
        }

        function setMergeFiles(mergeFiles = false) {
            product.MergeFiles = mergeFiles;
        }

        function setEPSMirroring(mirror, epsId = null) {
            let eps = getEps(epsId);
            eps.mirror = mirror;
        }

        function setEpsMergeOutput(merge, epsId = null) {
            let eps = getEps(epsId);
            eps.mergeOutput = merge;
        }

        function getPreviewImage() {
            const preview = getPreview();
            return preview.imagePath;
        }

        function getPreviewBgColor() {
            const preview = getPreview();
            return preview.bgColor;
        }

        function setNeedColorsUpgrade(state) {
            needsColorUpgrade = state;
        }

        function getNeedColorsUpgrade() {
            return needsColorUpgrade;
        }

        function setEditMode(state) {
            isProductEditMode = state;
        }

        function getEditMode() {
            return isProductEditMode;
        }

        /**
         * Set the product preview image as a relative path in the form of:
         *     '/Content/product-images/my-image.png'
         */
        function setPreviewImage(imagePath) {
            const preview = getPreview();
            preview.imagePath = imagePath;
        }

        function setPreviewBgColor(color) {
            const preview = getPreview();
            preview.bgColor = color;
        }

        function setPreviewImgSize(width, height) {
            const preview = getPreview();
            preview.width = width;
            preview.height = height;
        }

        function setProductSnapshotUrl(imagePath) {
            product.snapshotUrl = imagePath;
        }

        function getProductSnapshotUrl() {
            return product.snapshotUrl;
        }

        function setProductThumbnail(imagePath) {
            const preview = getPreview();
            preview.thumbnailPath = imagePath;
        }

        function getProductThumbnail() {
            const preview = getPreview();
            return preview.thumbnailPath;
        }

        /**
         * Add a circular text for the preview image
         * @param {Object}  data Object containing all circular text properties
         */
        function addPreviewCircularText(data, multiPreview = false) {
            // Set default values for optional properties
            const { pageEpsId = null, flipX = false, flipY = false, testingText = '', ...rest } = data;

            const text = {
                ...rest,
                pageEpsId,
                flipX,
                flipY,
                testingText,
                // Transform objects to JSON strings if needed
                color: typeof rest.color === 'object' ? JSON.stringify(rest.color) : rest.color,
                // Use maxSizePx as fontSize if available, otherwise use fontSize
                fontSize: rest.maxSizePx ?? rest.fontSize,
                addedOnEdit: rest.addedOnEdit // Internal property, not stored in the database
            };

            const listPreview = multiPreview ? getListPreview() : [getPreview()];

            for (const preview of listPreview) {
                const exist = preview.circularTextsPreview.find((item) => item.uuid === text.uuid);
                if (!exist) {
                    preview.circularTextsPreview.push(text);
                }
            }
            return text;
        }

        /**
         * Add a text for the preview image
         * @param {Object}  data Object containing all text properties
         */
        function addPreviewText(data, multiPreview = false) {
            // Set default values for optional properties
            const {
                pageEpsId = null,
                flipX = false,
                flipY = false,
                testingText = '',
                isTextPath = false,
                transformOptions = {},
                strokeColor = '',
                strokeWidth = 0,
                strokeColorsMap = null,
                strokeColorLibraryId = null,
                shadowType = null,
                shadowOffset = 0,
                shadowAngle = 0,
                shadowBlur = 0,
                shadowColor = '',
                shadowColorsMap = null,
                shadowColorLibraryId = null,
                metadata = '',
                ...rest
            } = data;

            const text = {
                ...rest,
                pageEpsId,
                flipX,
                flipY,
                testingText,
                isTextPath,
                transformOptions,
                color: typeof rest.color === 'object' ? JSON.stringify(rest.color) : rest.color,
                outlineColor:
                    typeof rest.outlineColor === 'object' ? JSON.stringify(rest.outlineColor) : rest.outlineColor,
                Version: rest.version, // Map version to Version for backward compatibility
                addedOnEdit: rest.addedOnEdit, // Internal property, not stored in the database
                strokeColor: typeof strokeColor === 'object' ? JSON.stringify(strokeColor) : strokeColor,
                strokeWidth,
                strokeColorsMap,
                strokeColorLibraryId,
                shadowType,
                shadowOffset,
                shadowAngle,
                shadowBlur,
                shadowColor: typeof shadowColor === 'object' ? JSON.stringify(shadowColor) : shadowColor,
                shadowColorsMap,
                shadowColorLibraryId,
                metadata
            };

            const listPreview = multiPreview ? getListPreview() : [getPreview()];

            for (const preview of listPreview) {
                const exist = preview.textsPreview.find((item) => item.uuid === text.uuid);
                if (!exist) {
                    preview.textsPreview.push(text);
                }
            }
            return text;
        }

        /**
         * Delete the linked preview element for the given element in the production file.
         * @param {Object} object Canvas object on the production file to delete on the preview.
         */
        function removeFromPreview(object, objDelete = true) {
            // Use UUID if available otherwise fallback to ID
            let uuIdOrId = object.uuid || object.id;
            const listPreview = getListPreview();
            for (const preview of listPreview) {
                if (!preview) {
                    return;
                }

                if (typeof uuIdOrId === 'undefined' || uuIdOrId == null) return;
                let indexText = preview.textsPreview.findIndex(function (x) {
                    return x.uuid === uuIdOrId || x.id === uuIdOrId;
                });
                if (indexText >= 0) {
                    preview.textsPreview[indexText].deleteFromEps = objDelete;
                }

                let indexCircularText = preview.circularTextsPreview.findIndex(function (x) {
                    return x.uuid === uuIdOrId || x.id === uuIdOrId;
                });
                if (indexCircularText >= 0) {
                    preview.circularTextsPreview[indexCircularText].deleteFromEps = objDelete;
                }

                let indexPlaceholder = preview.imagePlaceHoldersPreview.findIndex(function (x) {
                    return x.uuid === uuIdOrId || x.id === uuIdOrId;
                });
                if (indexPlaceholder >= 0) {
                    preview.imagePlaceHoldersPreview[indexPlaceholder].deleteFromEps = objDelete;
                }

                let indexVectorEPS = preview.vectorEPSPreview.findIndex(function (x) {
                    return x.uuid === uuIdOrId || x.id === uuIdOrId;
                });
                if (indexVectorEPS >= 0) {
                    preview.vectorEPSPreview[indexVectorEPS].deleteFromEps = objDelete;
                }

                let indexGroup = getGroups().findIndex(function (x) {
                    return x.uuid === uuIdOrId || x.id === uuIdOrId;
                });
                if (indexGroup >= 0) {
                    let group = getGroups()[indexGroup];
                    group.deleteFromEps = objDelete;
                    if (!objDelete) group.addedOnEdit = false;
                }
            }
        }

        function recoverFromPreview(object) {
            removeFromPreview(object, false);
        }

        function clearObjectsDeleteOnEPS() {
            const listPreview = getListPreview();
            for (const preview of listPreview) {
                preview.textsPreview = preview.textsPreview.filter(function (x) {
                    return !x.deleteFromEps;
                });
                preview.circularTextsPreview = preview.circularTextsPreview.filter(function (x) {
                    return !x.deleteFromEps;
                });
                preview.imagePlaceHoldersPreview = preview.imagePlaceHoldersPreview.filter(function (x) {
                    return !x.deleteFromEps;
                });
                preview.vectorEPSPreview = preview.vectorEPSPreview.filter(function (x) {
                    return !x.deleteFromEps;
                });
                product.groups = product.groups.filter(function (x) {
                    return !x.deleteFromEps;
                });
            }
        }

        /**
         * Add a Image for the Preview
         * @param {Object}  data
         */
        function addPreviewPlaceholderImage(data, multiPreview = false) {
            // Set default values for optional properties
            const {
                CustomilyRemoveBackground = false,
                CustomilyFaceCutOut = false,
                CustomilyImageFilter = 0,
                pageEpsId = null,
                flipX = false,
                flipY = false,
                imageToImage = false,
                imageToImageConfig = null,
                imageEffects = null,
                CrosswordConfig = null,
                calendar = false,
                calendarConfig = null,
                enablePerspective = false,
                perspectiveCoords = null,
                isMoonPhase = false,
                ...rest
            } = data;

            const image = {
                ...rest,
                CustomilyRemoveBackground,
                CustomilyFaceCutOut,
                CustomilyImageFilter,
                pageEpsId,
                flipX,
                flipY,
                imageToImage,
                imageToImageConfig,
                imageEffects,
                CrosswordConfig,
                calendar,
                calendarConfig,
                enablePerspective,
                perspectiveCoords:
                    perspectiveCoords instanceof Array ? JSON.stringify(perspectiveCoords) : perspectiveCoords,
                isMoonPhase,
                // Transform objects to JSON strings if needed
                color: typeof rest.color === 'object' ? JSON.stringify(rest.color) : rest.color,
                dynamicImagesPath:
                    rest.dynamicImagesPath instanceof Map
                        ? JSON.stringify(Array.from(rest.dynamicImagesPath.entries()))
                        : rest.dynamicImagesPath,
                addedOnEdit: rest.addedOnEdit // Internal property, not stored in the database
            };

            const listPreview = multiPreview ? getListPreview() : [getPreview()];

            for (const preview of listPreview) {
                const exist = preview.imagePlaceHoldersPreview.find((item) => item.uuid === image.uuid);
                if (!exist) {
                    preview.imagePlaceHoldersPreview.push(image);
                }
            }
            return image;
        }

        /**
         * Add a vector EPS for the preview image
         * @param {Object}  data Object containing all vector EPS properties
         */
        function addPreviewVectorEPS(data, multiPreview = false) {
            // Set default values for optional properties
            const { pageEpsId = null, flipX = false, flipY = false, ...rest } = data;

            const eps = {
                ...rest,
                pageEpsId,
                flipX,
                flipY,
                addedOnEdit: rest.addedOnEdit // Internal property, not stored in the database
            };

            // Handle PDF vs SVG content
            if (rest.contentIsPdf) {
                eps.pdf = rest.vectorContent;
            } else {
                eps.svgString = rest.vectorContent;
            }

            const listPreview = multiPreview ? getListPreview() : [getPreview()];

            for (const preview of listPreview) {
                const exist = preview.vectorEPSPreview.find((item) => item.uuid === eps.uuid);
                if (!exist) {
                    preview.vectorEPSPreview.push(eps);
                }
            }

            return eps;
        }

        function setEPSSize(width, height, epsId = null) {
            let eps = getEps(epsId);
            eps.width = width;
            eps.height = height;
        }

        function setEPSdpi(dpi, epsId = null) {
            let eps = getEps(epsId);
            eps.dpi = dpi;
        }

        function setExportPPI(ppi, epsId = null) {
            let eps = getEps(epsId);
            eps.exportPPI = ppi;
        }

        /**
         * Add a text for the EPS
         * @param {Number}  centerX     Center X position of the text in the EPS in pixels
         * @param {Number}  centerY     Center Y position of the text in the EPS in pixels
         * @param {String}  color       Color string in the form of #rrggbb
         * @param {Number}  rotation    Text rotation in degrees
         * @param {Number}  skewX       Horizontal skew in degrees
         * @param {Number}  skewY       Vertical skew in degrees
         * @param {Number}  width       Text bounds width in pixels
         * @param {Number}  height      Text bounds height in pixels
         * @param {Number}  minSizePx   Minimum font size in pixels
         * @param {Number}  maxSizePx   Maximum font size in pixels
         * @param {Bool}    multiline   If multiline is allowed
         * @param {Bool}    caps        If text should be capitalized
         * @param {String}  fontPath    Path to the font to use in the form of /Content/fonts/arial.ttf
         * @param {String}  textAlign   Text alignment, valid values are 'center', 'left', 'right' and 'justify'
         * @param {String}  outlineColor    Color string of the text outline in the form of #rrggbb
         * @param {Number}  outlineWidth    Outline width (integer). If no outline is applied this should be 0
         * @param {Number}  zIndex      z-index of this element, 0 being the deepest level
         * @param {Number}  id          Unique id of this text
         * @param {String}  uuid        Unique identifier of this text
         * @param {Map}     fontsMap    Fonts Map
         * @param {Map}     fontColorsMap    Font Color Map
         * @param {String}  Itext       Fabric object
         * @param {Object}  editedProps Edited properties
         * @param {Object}  editedSize  Edited size
         * @param {Number}  tracking    Tracking amount
         * @param {Number}  lineSpacing Line spacing
         * @param {Number}  colorLibraryId Color Library Id
         * @param {Number}  fontLibraryId Font Library Id
         * @param {Number}  version     Version
         * @param {Boolean} locked      Locked
         * @param {Boolean} visible     Visible
         * @param {String}  name        Name
         * @param {String}  defaultValue Default Value
         * @param {String}  prefix      Prefix
         * @param {String}  suffix      Suffix
         * @param {Boolean} ligatures   Ligatures
         * @param {String}  referenceId Reference Id
         * @param {Boolean} isPrintArea Is Print Area
         * @param {Boolean} isOverlay   Is Overlay
         * @param {Number}  groupId     Group Id
         * @param {String}  textVerticalAlign   Text vertical alignment, valid values are 'center', 'top' and 'bottom'
         * @param {Boolean} allowEdit   Allow Edit
         * @param {Number}  pageEpsId   Page EPS Id
         * @param {Boolean} flipX       Flip X
         * @param {Boolean} flipY       Flip Y
         * @param {Array}   textures    Textures
         * @param {String}  testingText Testing Text
         * @param {Boolean} isTextPath  Is Text Path
         * @param {Object}  transformOptions Transform Options
         * @param {Boolean}  useAsMask Use text as mask
         * @param {Bool}    verticalText   If verticalText is enabled
         * @returns {Number} index of the newly added element
         */
        function addEPSText(
            centerX,
            centerY,
            color,
            rotation,
            skewX,
            skewY,
            width,
            height,
            minSizePx,
            maxSizePx,
            multiline,
            caps,
            fontPath,
            textAlign,
            outlineColor,
            outlineWidth,
            outlineColorsMap,
            outlineColorLibraryId,
            zIndex,
            id,
            uuid,
            fontsMap,
            fontColorsMap,
            Itext,
            editedProps,
            editedSize,
            tracking,
            lineSpacing,
            colorLibraryId,
            fontLibraryId,
            version,
            locked,
            visible,
            name,
            defaultValue,
            prefix,
            suffix,
            ligatures,
            referenceId,
            isPrintArea,
            isOverlay,
            groupId,
            textVerticalAlign,
            allowEdit,
            oldAllowEdit,
            initCenterX,
            initCenterY,
            initRotation,
            initFontSize,
            pageEpsId = null,
            flipX = false,
            flipY = false,
            textures,
            testingText = '',
            isTextPath = false,
            transformOptions = null,
            useAsMask = false,
            verticalText = false,
            autofillSource,
            dateFormat,
            strokeColor = '',
            strokeWidth = 0,
            strokeColorsMap = new Map(),
            strokeColorLibraryId = null,
            shadowType,
            shadowOffset,
            shadowAngle,
            shadowBlur,
            shadowColor,
            shadowColorsMap,
            shadowColorLibraryId,
            metadata
        ) {
            let eps = getEps(pageEpsId);
            if (fontsMap) {
                //if it has something stored on the map -> transform it to JSON because the database won't allow us to save a map
                fontsMap = JSON.stringify(Array.from(fontsMap.entries()));
            }
            if (fontColorsMap) {
                //if it has something stored on the map -> transform it to JSON because the database won't allow us to save a map
                fontColorsMap = JSON.stringify(Array.from(fontColorsMap.entries()));
            }

            if (outlineColorsMap) {
                outlineColorsMap = JSON.stringify(Array.from(outlineColorsMap.entries()));
            }

            if (strokeColorsMap) {
                strokeColorsMap = JSON.stringify(Array.from(strokeColorsMap.entries()));
            }

            if (shadowColorsMap) {
                shadowColorsMap = JSON.stringify(Array.from(shadowColorsMap.entries()));
            }

            let text = {
                id: id,
                uuid: uuid,
                centerX: centerX,
                centerY: centerY,
                color: typeof color === 'object' ? JSON.stringify(color) : color,
                rotation: rotation,
                width: width,
                height: height,
                minSizePx: minSizePx,
                maxSizePx: maxSizePx,
                multiline: multiline,
                verticalText: verticalText,
                caps: caps,
                fontPath: fontPath,
                textAlign: textAlign,
                outlineColor: typeof outlineColor === 'object' ? JSON.stringify(outlineColor) : outlineColor,
                outlineWidth: outlineWidth,
                outlineColorsMap,
                outlineColorLibraryId,
                skewX: skewX,
                skewY: skewY,
                zIndex: zIndex,
                fontsMap: fontsMap,
                fontColorsMap: fontColorsMap,
                Itext: Itext,
                editedProps: editedProps,
                editedSize: editedSize,
                tracking: tracking,
                lineSpacing: lineSpacing,
                colorLibraryId: colorLibraryId,
                fontLibraryId: fontLibraryId,
                Version: version,
                locked: locked,
                visible: visible,
                name: name,
                defaultValue: defaultValue,
                prefix: prefix,
                suffix: suffix,
                ligatures: ligatures,
                referenceId: referenceId,
                isPrintArea: isPrintArea,
                isOverlay: isOverlay,
                groupId: groupId,
                textVerticalAlign,
                allowEdit,
                oldAllowEdit,
                initCenterX,
                initCenterY,
                initRotation,
                initFontSize,
                pageEpsId,
                flipX,
                flipY,
                textures,
                testingText,
                isTextPath,
                transformOptions,
                useAsMask,
                autofillSource,
                dateFormat,
                strokeColor: typeof strokeColor === 'object' ? JSON.stringify(strokeColor) : strokeColor,
                strokeWidth,
                strokeColorsMap: strokeColorsMap,
                strokeColorLibraryId: strokeColorLibraryId,
                shadowType,
                shadowOffset,
                shadowAngle,
                shadowBlur,
                shadowColor: typeof shadowColor === 'object' ? JSON.stringify(shadowColor) : shadowColor,
                shadowColorsMap,
                shadowColorLibraryId,
                metadata: typeof metadata === 'object' ? JSON.stringify(metadata) : metadata
            };
            let size = eps.textsEps.push(text);
            return size - 1;
        }

        /**
         * Add a text to the product from the given object
         * @param {BoundedText} canvasObject Object to add to the product
         */
        function addEPSTextFromObject(canvasObject, skipPreviewElements = false) {
            return $q(function (resolve, reject) {
                // This is to prevent having the last selected font saved as the default one
                //  in the cases where there are several fonts stored in the same text placeholder we force the one in position (1) to be the default one,
                //  in other words, we store the first font in the fontsMap into fontPath
                if (canvasObject.fontsMap) {
                    canvasObject.fontPath = canvasObject.fontsMap.get(1);
                }
                if (canvasObject.fontColorsMap) {
                    canvasObject.color = canvasObject.fontColorsMap.get(1);
                }
                if (canvasObject.outlineColorsMap) {
                    canvasObject.outlineColor = canvasObject.outlineColorsMap.get(1);
                }
                if (canvasObject.strokeColorsMap) {
                    canvasObject.strokeColor = canvasObject.strokeColorsMap.get(1);
                }
                if (canvasObject.shadowColorsMap) {
                    canvasObject.shadowColor = canvasObject.shadowColorsMap.get(1);
                }

                const isTextPath = canvasObject.type === 'text-transform';

                let addedEpsElementIndex = addEPSText(
                    canvasObject.left,
                    canvasObject.top,
                    canvasObject.color,
                    canvasObject.angle,
                    canvasObject.skewX,
                    canvasObject.skewY,
                    canvasObject.getWidth(),
                    canvasObject.getHeight(),
                    canvasObject.minSizePx,
                    canvasObject.maxSizePx,
                    canvasObject.multiline,
                    canvasObject.caps,
                    canvasObject.fontPath,
                    canvasObject.getTextAlign(),
                    canvasObject.outlineColor,
                    canvasObject.getOutlineWidth(),
                    canvasObject.outlineColorsMap,
                    canvasObject.outlineColorLibraryId,
                    canvasObject.zIndex,
                    canvasObject.id,
                    canvasObject.uuid,
                    canvasObject.fontsMap,
                    canvasObject.fontColorsMap,
                    canvasObject.Itext,
                    canvasObject.editedProps,
                    canvasObject.editedSize,
                    canvasObject.getTrackingAmount(),
                    canvasObject.lineSpacing,
                    canvasObject.colorLibraryId,
                    canvasObject.fontLibraryId,
                    canvasObject.version,
                    canvasObject.locked,
                    canvasObject.visible,
                    canvasObject.name,
                    canvasObject.defaultValue,
                    canvasObject.prefix,
                    canvasObject.suffix,
                    canvasObject.ligatures,
                    canvasObject.referenceId,
                    canvasObject.isPrintArea,
                    canvasObject.isOverlay,
                    canvasObject.groupId,
                    canvasObject.textVerticalAlign,
                    canvasObject.allowEdit,
                    canvasObject.oldAllowEdit,
                    /*If the text uses the previous version of allowEdit (without a bounding box), these properties are left as null.
                    Once they have a value, the new version is considered active.*/
                    !canvasObject.oldAllowEdit && canvasObject.getTextLeft ? canvasObject.getTextLeft() : null,
                    !canvasObject.oldAllowEdit && canvasObject.getTextTop ? canvasObject.getTextTop() : null,
                    !canvasObject.oldAllowEdit && canvasObject.getTextAngle ? canvasObject.getTextAngle() : null,
                    !canvasObject.oldAllowEdit && canvasObject.getFontSize ? canvasObject.getFontSize() : null,
                    /* --- */
                    canvasObject.pageEpsId,
                    canvasObject.getFlip().flipX,
                    canvasObject.getFlip().flipY,
                    canvasObject.textures,
                    canvasObject.text,
                    isTextPath,
                    canvasObject.transformOptions || null,
                    canvasObject.useAsMask || false,
                    canvasObject.verticalText || false,
                    canvasObject.autofillSource || null,
                    canvasObject.dateFormat || {},
                    /* stroke */
                    canvasObject.strokeColor || null,
                    (canvasObject.getStrokeWidth && canvasObject.getStrokeWidth()) || 0,
                    canvasObject.strokeColorsMap || null,
                    canvasObject.strokeColorLibraryId || null,
                    /* shadow */
                    canvasObject.shadowType,
                    canvasObject.shadowOffset || 0,
                    canvasObject.shadowAngle || 0,
                    canvasObject.shadowBlur || 0,
                    canvasObject.shadowColor || null,
                    canvasObject.shadowColorsMap || null,
                    canvasObject.shadowColorLibraryId || null,
                    canvasObject.metadata || ''
                );
                updateProductionElementId(canvasObject, canvasObject.id);

                if (skipPreviewElements) {
                    resolve();
                    return;
                }

                // Added text on edit mode
                if (canvasObject.addedOnEdit) {
                    const objectCenterX = canvasObject.left;
                    const objectCenterY = canvasObject.top;
                    const boundingRectWidth = canvasObject.width;
                    const boundingRectHeight = canvasObject.height;
                    addPreviewText(
                        {
                            centerX: objectCenterX,
                            centerY: objectCenterY,
                            color: canvasObject.color,
                            rotation: canvasObject.angle,
                            skewX: canvasObject.skewX,
                            skewY: canvasObject.skewY,
                            width: boundingRectWidth,
                            height: boundingRectHeight,
                            minSizePx: canvasObject.minSizePx,
                            maxSizePx: canvasObject.maxSizePx,
                            multiline: canvasObject.multiline,
                            caps: canvasObject.caps,
                            fontPath: canvasObject.fontPath,
                            textAlign: canvasObject.getTextAlign(),
                            outlineColor: canvasObject.outlineColor,
                            outlineWidth: canvasObject.getOutlineWidth(),
                            outlineColorsMap: canvasObject.outlineColorsMap,
                            outlineColorLibraryId: canvasObject.outlineColorLibraryId,
                            zIndex: canvasObject.zIndex,
                            id: canvasObject.id,
                            uuid: canvasObject.uuid,
                            fontsMap: canvasObject.fontsMap,
                            fontColorsMap: canvasObject.fontColorsMap,
                            Itext: canvasObject.Itext,
                            textures: canvasObject.textures,
                            tracking: canvasObject.getTrackingAmount(),
                            lineSpacing: canvasObject.lineSpacing,
                            colorLibraryId: canvasObject.colorLibraryId,
                            fontLibraryId: canvasObject.fontLibraryId,
                            version: canvasObject.version,
                            addedOnEdit: true,
                            locked: canvasObject.locked,
                            visible: canvasObject.visible,
                            name: canvasObject.name,
                            prefix: canvasObject.prefix,
                            suffix: canvasObject.suffix,
                            ligatures: canvasObject.ligatures,
                            referenceId: canvasObject.referenceId,
                            isPrintArea: canvasObject.isPrintArea,
                            isOverlay: canvasObject.isOverlay,
                            groupId: previewGroupSync.resolveTargetGroupId(canvasObject.groupId),
                            textVerticalAlign: canvasObject.textVerticalAlign,
                            allowEdit: canvasObject.allowEdit,
                            oldAllowEdit: canvasObject.oldAllowEdit,
                            /*If the text uses the previous version of allowEdit (without a bounding box), these properties are left as null.
                        Once they have a value, the new version is considered active.*/
                            initCenterX:
                                !canvasObject.oldAllowEdit && canvasObject.getTextLeft
                                    ? canvasObject.getTextLeft()
                                    : null,
                            initCenterY:
                                !canvasObject.oldAllowEdit && canvasObject.getTextTop
                                    ? canvasObject.getTextTop()
                                    : null,
                            initRotation:
                                !canvasObject.oldAllowEdit && canvasObject.getTextAngle
                                    ? canvasObject.getTextAngle()
                                    : null,
                            initFontSize:
                                !canvasObject.oldAllowEdit && canvasObject.getFontSize
                                    ? canvasObject.getFontSize()
                                    : null,
                            /* --- */
                            pageEpsId: canvasObject.pageEpsId,
                            flipX: canvasObject.getFlip().flipX || false,
                            flipY: canvasObject.getFlip().flipY || false,
                            testingText: canvasObject.text,
                            isTextPath: isTextPath || false,
                            transformOptions: canvasObject.transformOptions || null,
                            clipPath: '',
                            useAsMask: canvasObject.useAsMask || false,
                            verticalText: canvasObject.verticalText || false,
                            autofillSource: canvasObject.autofillSource || '',
                            dateFormat: canvasObject.dateFormat || {},
                            /* stroke */
                            strokeColor: canvasObject.strokeColor || null,
                            strokeWidth: (canvasObject.getStrokeWidth && canvasObject.getStrokeWidth()) || 0,
                            strokeColorsMap: canvasObject.strokeColorsMap || null,
                            strokeColorLibraryId: canvasObject.strokeColorLibraryId || null,
                            /* shadow */
                            shadowType: canvasObject.shadowType,
                            shadowOffset: canvasObject.shadowOffset || 0,
                            shadowAngle: canvasObject.shadowAngle || 0,
                            shadowBlur: canvasObject.shadowBlur || 0,
                            shadowColor: canvasObject.shadowColor || null,
                            shadowColorsMap: canvasObject.shadowColorsMap || null,
                            shadowColorLibraryId: canvasObject.shadowColorLibraryId || null,
                            metadata: canvasObject.metadata || ''
                        },
                        true
                    );
                    resolve();
                } else {
                    const preview = getPreview();
                    const updateEditedElement = function (epsElementToCheck, epsElement, previewElement, eps) {
                        // If we are editing the product, preview.textsPreview won't be empty.
                        // If it is it means that we pressed the back button from step2 without saving the product, so in this case
                        //  we won't recalculate anything, as we'll be creating step2 elements as if it were the first item.

                        if (preview.textsPreview && previewElement && epsElement && epsElementToCheck) {
                            console.log(preview.textsPreview, previewElement, epsElement, epsElementToCheck);
                            // If the size changed, we have to recompute the size on the preview to keep the proportions the same
                            if (epsElementToCheck.editedSize) {
                                // Compute the element width and height
                                let scale = Math.min(preview.width / eps.width, preview.height / eps.height);
                                let boundingRectWidth = epsElement.width * scale;
                                let boundingRectHeight = epsElement.height * scale;

                                // Update the element size in the preview
                                previewElement.width = boundingRectWidth;
                                previewElement.height = boundingRectHeight;

                                // Calculate the font sizes
                                let sizes = GeneralMathUtils.calculateFontSizesFromDimensions(
                                    boundingRectWidth,
                                    epsElement.width,
                                    epsElement.minSizePx,
                                    epsElement.maxSizePx
                                );
                                previewElement.minSizePx = sizes.min;
                                previewElement.maxSizePx = sizes.max;
                            }

                            // Copy edited props to the preview element
                            for (const prop of epsElementToCheck.editedProps) {
                                if (prop === 'textAlign') {
                                    previewElement.textAlign = epsElement.getTextAlign();
                                } else if (prop === 'tracking') {
                                    previewElement.tracking = epsElement.getTrackingAmount();
                                } else if (prop === 'outlineWidth') {
                                    const outlineWidth = GeneralMathUtils.calculateStrokeSizeFromDimensions(
                                        previewElement.width,
                                        epsElement.width,
                                        epsElement.getOutlineWidth()
                                    );
                                    previewElement.outlineWidth = outlineWidth;
                                } else if (prop == 'strokeWidth') {
                                    const strokeWidth = GeneralMathUtils.calculateStrokeSizeFromDimensions(
                                        previewElement.width,
                                        epsElement.width,
                                        epsElement.getStrokeWidth()
                                    );
                                    previewElement.strokeWidth = strokeWidth;
                                } else if (prop === 'transformOptions') {
                                    previewElement.transformOptions = epsElement.transformOptions || {};
                                    previewElement.needUpdatePreview = true;
                                } else if (
                                    previewGroupSync.syncTargetElementGroupEdit(
                                        previewElement,
                                        prop,
                                        epsElement.groupId
                                    )
                                ) {
                                } else if (prop === 'innerText') {
                                    const values = GeneralMathUtils.calculateInitTextValues(
                                        previewElement.width,
                                        epsElement.width,
                                        epsElement.getTextLeft(),
                                        epsElement.getTextTop(),
                                        epsElement.getFontSize()
                                    );
                                    previewElement.initCenterX = values.initCenterX;
                                    previewElement.initCenterY = values.initCenterY;
                                    previewElement.initFontSize = values.initFontSize;
                                    previewElement.initRotation = epsElement.getTextAngle();
                                } else {
                                    previewElement[prop] = epsElement[prop];
                                }
                            }
                        }
                    };

                    // If the object was edited, update the parameters in the preview so it shows updated in step2
                    if (canvasObject.isEdited() || canvasObject.editedSize) {
                        let eps = getEps(canvasObject.pageEpsId);
                        // If the object doesn't have UUID use the old method to find the element in the preview
                        if (!canvasObject.uuid) {
                            let indexes = getAllIndexes(eps.textsEps, canvasObject.id);

                            if (indexes.length > 0) {
                                let duplicatedIds = true;

                                // Only one text with this ID? Find the index of the preview element with this ID.
                                // If we have more than one index that means we have duplicated texts IDs so just iterate
                                //  the previews element assuming they are in the same order as in the eps THIS IS NOT RIGHT
                                //  AND CAN FAIL, OLDER PRODUCTS WITH DUPLICATED IDs SHOULD BE RE-CREATED SO ELEMENTS ARE ASSIGNED
                                //  WITH A UUID INSTEAD
                                if (indexes.length == 1) {
                                    duplicatedIds = false;

                                    // Find the index of the preview element
                                    let index = preview.textsPreview.findIndex(function (obj) {
                                        return obj.id === canvasObject.id;
                                    });

                                    if (index > -1) {
                                        indexes = [index];
                                    }
                                }

                                let occurrence = 0;
                                let lastElement = null;
                                for (const index of indexes) {
                                    // If we don't have duplicated ids don't assume elements order, find the EPS element
                                    //  by its id
                                    let epsIndex = index;
                                    if (!duplicatedIds) {
                                        epsIndex = addedEpsElementIndex;
                                    }
                                    const epsText = eps.textsEps[epsIndex];
                                    let previewText = preview.textsPreview[index];

                                    // If the preview element id doesn't match the production or the preview element was not even found it's probably
                                    //  because the order of the elements for production and preview was not preserved, try to find it by id and occurrence
                                    //  as a best effort to find some element
                                    if (
                                        !previewText ||
                                        previewText.id !== epsText.id ||
                                        (lastElement && lastElement === previewText)
                                    ) {
                                        const previewTexts = preview.textsPreview.filter(
                                            (e) => e.id === canvasObject.id
                                        );
                                        previewText = previewTexts[occurrence];

                                        // Don't select the same text more than once if we have the case where production and preview elements don't have
                                        //  the same order
                                        let n = occurrence;
                                        while (previewTexts.length > 1 && lastElement && lastElement === previewText) {
                                            previewText = previewTexts[++n];
                                        }

                                        lastElement = previewText;
                                    }

                                    if (previewText && previewText.id === epsText.id) {
                                        updateEditedElement(epsText, canvasObject, previewText, eps);
                                    } else {
                                        reject(new Error('Could not edit the text element with ID ' + canvasObject.id));
                                        return;
                                    }

                                    ++occurrence;
                                }
                            } else if (preview.textsPreview.length > 0) {
                                reject(new Error('Could not edit the text element with ID ' + canvasObject.id));
                                return;
                            }
                        } else {
                            // If the element has a UUID find the preview elements by UUID
                            let textPreview = preview.textsPreview.find((t) => t.uuid === canvasObject.uuid);
                            if (!textPreview) {
                                console.error("Couldn't find preview element with UUID: " + canvasObject.uuid);
                            }
                            updateEditedElement(canvasObject, canvasObject, textPreview, eps);
                        }
                    }
                    resolve();
                }
            });
        }

        /**
         * Add a circular text for the EPS
         * @param {Number}  centerX     Center X position of the text in the preview image in pixels
         * @param {Number}  centerY     Center Y position of the text in the preview image in pixels
         * @param {String}  color       Color string in the form of #rrggbb
         * @param {Number}  rotation    Text rotation in degrees
         * @param {Number}  skewX       Horizontal skew in degrees
         * @param {Number}  skewY       Vertical skew in degrees
         * @param {Number}  horizontalDiameter      Horizontal diameter in pixels
         * @param {Number}  verticalDiameter        Vertical diameter in pixels
         * @param {Number}  fontSize    Font size in pixels
         * @param {Bool}    caps        If text should be capitalized
         * @param {String}  fontPath    Path to the font to use in the form of /Content/fonts/arial.ttf
         * @param {String}  textAlign   Text alignment, valid values are 'center', 'left', 'right' and 'justify'
         * @param {Bool}    convex      Wether the text is convex or not (concave)
         * @param {String}  outlineColor    Color string of the text outline in the form of #rrggbb
         * @param {Number}  outlineWidth    Outline width (integer). If no outline is applied this should be 0
         * @param {Number}  zIndex      z-index of this element, 0 being the deepest level
         * @param {Number}  id          Unique id of this text
         * @param {Map}     fontsMap Fonts Map
         * @param {Map}     fontColorsMap Font Colors Map
         */
        function addEPSCircularText(
            centerX,
            centerY,
            color,
            rotation,
            skewX,
            skewY,
            horizontalDiameter,
            verticalDiameter,
            fontSize,
            minSizePx,
            maxSizePx,
            caps,
            fontPath,
            textAlign,
            convex,
            tracking,
            outlineColor,
            outlineWidth,
            outlineColorsMap,
            outlineColorLibraryId,
            zIndex,
            id,
            uuid,
            fontsMap,
            fontColorsMap,
            colorLibraryId,
            fontLibraryId,
            startAngle,
            endAngle,
            locked,
            visible,
            name,
            defaultValue,
            prefix,
            suffix,
            referenceId,
            isPrintArea,
            isOverlay,
            groupId,
            pageEpsId = null,
            flipX = false,
            flipY = false,
            testingText = ''
        ) {
            let eps = getEps(pageEpsId);
            if (fontsMap) {
                //if it has something stored on the map -> transform it to JSON because the database won't allow us to save a map
                fontsMap = JSON.stringify(Array.from(fontsMap.entries()));
            }
            if (fontColorsMap) {
                //if it has something stored on the map -> transform it to JSON because the database won't allow us to save a map
                fontColorsMap = JSON.stringify(Array.from(fontColorsMap.entries()));
            }

            if (outlineColorsMap) {
                outlineColorsMap = JSON.stringify(Array.from(outlineColorsMap.entries()));
            }

            let text = {
                id: id,
                uuid: uuid,
                centerX: centerX,
                centerY: centerY,
                color: typeof color === 'object' ? JSON.stringify(color) : color,
                rotation: rotation,
                horizontalDiameter: horizontalDiameter,
                verticalDiameter: verticalDiameter,
                fontSize: maxSizePx ?? fontSize,
                minSizePx,
                maxSizePx,
                caps: caps,
                fontPath: fontPath,
                textAlign: textAlign,
                convex: convex,
                tracking,
                outlineColor: outlineColor,
                outlineWidth: outlineWidth,
                outlineColorsMap,
                outlineColorLibraryId,
                skewX: skewX,
                skewY: skewY,
                zIndex: zIndex,
                // add fonts map
                fontsMap: fontsMap,
                // add font colors map
                fontColorsMap: fontColorsMap,
                colorLibraryId: colorLibraryId,
                fontLibraryId: fontLibraryId,
                startAngle: startAngle,
                endAngle: endAngle,
                locked: locked,
                visible: visible,
                name: name,
                defaultValue: defaultValue,
                prefix: prefix,
                suffix: suffix,
                referenceId: referenceId,
                isPrintArea: isPrintArea,
                isOverlay: isOverlay,
                groupId: groupId,
                pageEpsId,
                flipX,
                flipY,
                testingText
            };
            eps.circularTextsEps.push(text);
        }

        /**
         * Add a circular text to the product from the given object
         * @param {PathIText} canvasObject Object to add to the product
         */
        function addEPSCircularTextFromObject(canvasObject, skipPreviewElements = false) {
            return $q(function (resolve, reject) {
                const preview = getPreview();
                // This is to prevent having the last selected font saved as the default one
                // In the cases where there are several fonts stored in the same text placeholder we force the one in position (1) to be the default one
                // In other words, we store the first font in the fontsMap into fontPath
                if (canvasObject.fontsMap) {
                    canvasObject.fontPath = canvasObject.fontsMap.get(1);
                }
                if (canvasObject.fontColorsMap) {
                    canvasObject.color = canvasObject.fontColorsMap.get(1);
                }
                if (canvasObject.outlineColorsMap) {
                    canvasObject.outlineColor = canvasObject.outlineColorsMap.get(1);
                }

                addEPSCircularText(
                    canvasObject.left,
                    canvasObject.top,
                    canvasObject.color,
                    canvasObject.angle,
                    canvasObject.skewX,
                    canvasObject.skewY,
                    canvasObject.horizontalDiameter,
                    canvasObject.verticalDiameter,
                    canvasObject.fontSize,
                    canvasObject.minSizePx,
                    canvasObject.maxSizePx,
                    canvasObject.caps,
                    canvasObject.fontPath,
                    canvasObject.textAlign,
                    canvasObject.convex,
                    canvasObject.tracking,
                    canvasObject.stroke,
                    canvasObject.strokeWidth,
                    canvasObject.outlineColorsMap,
                    canvasObject.outlineColorLibraryId,
                    canvasObject.zIndex,
                    canvasObject.id,
                    canvasObject.uuid,
                    canvasObject.fontsMap,
                    canvasObject.fontColorsMap,
                    canvasObject.colorLibraryId,
                    canvasObject.fontLibraryId,
                    canvasObject.startAngle,
                    canvasObject.endAngle,
                    canvasObject.locked,
                    canvasObject.visible,
                    canvasObject.name,
                    canvasObject.defaultValue,
                    canvasObject.prefix,
                    canvasObject.suffix,
                    canvasObject.referenceId,
                    canvasObject.isPrintArea,
                    canvasObject.isOverlay,
                    canvasObject.groupId,
                    canvasObject.pageEpsId,
                    canvasObject.getFlip().flipX,
                    canvasObject.getFlip().flipY,
                    canvasObject.text
                );
                updateProductionElementId(canvasObject, canvasObject.id);

                if (skipPreviewElements) {
                    resolve();
                    return;
                }

                // Added text on edit mode
                if (canvasObject.addedOnEdit) {
                    const objectCenterX = canvasObject.left;
                    const objectCenterY = canvasObject.top;
                    const horizontalDiameter = canvasObject.horizontalDiameter;
                    const verticalDiameter = canvasObject.verticalDiameter;

                    addPreviewCircularText(
                        {
                            centerX: objectCenterX,
                            centerY: objectCenterY,
                            color: canvasObject.color,
                            rotation: canvasObject.angle,
                            skewX: canvasObject.skewX,
                            skewY: canvasObject.skewY,
                            horizontalDiameter: horizontalDiameter,
                            verticalDiameter: verticalDiameter,
                            fontSize: canvasObject.fontSize,
                            minSizePx: canvasObject.minSizePx,
                            maxSizePx: canvasObject.maxSizePx,
                            caps: canvasObject.caps,
                            fontPath: canvasObject.fontPath,
                            textAlign: canvasObject.textAlign,
                            convex: canvasObject.convex,
                            tracking: canvasObject.tracking,
                            outlineColor: canvasObject.stroke,
                            outlineWidth: canvasObject.strokeWidth,
                            outlineColorsMap: canvasObject.outlineColorsMap,
                            outlineColorLibraryId: canvasObject.outlineColorLibraryId,
                            zIndex: canvasObject.zIndex,
                            id: canvasObject.id,
                            uuid: canvasObject.uuid,
                            // add fonts map
                            fontsMap: canvasObject.fontsMap,
                            // add font colors map
                            fontColorsMap: canvasObject.fontColorsMap,
                            colorLibraryId: canvasObject.colorLibraryId,
                            fontLibraryId: canvasObject.fontLibraryId,
                            startAngle: canvasObject.startAngle,
                            endAngle: canvasObject.endAngle,
                            addedOnEdit: true,
                            locked: canvasObject.locked,
                            visible: canvasObject.visible,
                            name: canvasObject.name,
                            prefix: canvasObject.prefix,
                            suffix: canvasObject.suffix,
                            referenceId: canvasObject.referenceId,
                            isPrintArea: canvasObject.isPrintArea,
                            isOverlay: canvasObject.isOverlay,
                            groupId: previewGroupSync.resolveTargetGroupId(canvasObject.groupId),
                            pageEpsId: canvasObject.pageEpsId,
                            flipX: false,
                            flipY: false,
                            testingText: canvasObject.text,
                            clipPath: null
                        },
                        true
                    );
                    resolve();
                } else {
                    // If the object was edited, update the parameters in the preview so it shows updated in step2
                    if (canvasObject.isEdited() || canvasObject.editedSize || canvasObject.fontEdited) {
                        let previewElement = null;
                        if (!canvasObject.uuid) {
                            // Find the index of the element
                            let index = preview.circularTextsPreview.findIndex(function (obj) {
                                return obj.id === canvasObject.id;
                            });
                            previewElement = preview.circularTextsPreview[index];
                        } else {
                            // Find the element by UUID
                            previewElement = preview.circularTextsPreview.find((t) => t.uuid === canvasObject.uuid);
                        }

                        if (previewElement) {
                            // If the size changed, we have to recompute the size on the preview to keep the proportions the same
                            if (canvasObject.editedSize) {
                                // Compute the element width and height
                                const eps = getEps(canvasObject.pageEpsId);
                                const scale = Math.min(preview.width / eps.width, preview.height / eps.height);
                                const horizontalDiameter = canvasObject.horizontalDiameter * scale;
                                const verticalDiameter = canvasObject.verticalDiameter * scale;

                                // Update the element size in the preview
                                previewElement.horizontalDiameter = horizontalDiameter;
                                previewElement.verticalDiameter = verticalDiameter;
                                // Calculate de preview fontSize
                                previewElement.fontSize = GeneralMathUtils.calculateCircularFontSizeFromDimensions(
                                    horizontalDiameter,
                                    canvasObject.horizontalDiameter,
                                    canvasObject.fontSize
                                );
                                const { min, max } = GeneralMathUtils.calculateFontSizesFromDimensions(
                                    horizontalDiameter,
                                    canvasObject.horizontalDiameter,
                                    canvasObject.minSizePx,
                                    canvasObject.maxSizePx
                                );
                                previewElement.minSizePx = min;
                                previewElement.maxSizePx = max;
                            }

                            // Copy edited props to the preview element
                            for (const prop of canvasObject.editedProps) {
                                if (
                                    previewGroupSync.syncTargetElementGroupEdit(
                                        previewElement,
                                        prop,
                                        canvasObject.groupId
                                    )
                                ) {
                                    continue;
                                }

                                previewElement[prop] = canvasObject[prop];
                                if (prop === 'strokeWidth') {
                                    const strokeWidth = GeneralMathUtils.calculateStrokeSizeFromDimensions(
                                        previewElement.horizontalDiameter,
                                        canvasObject.horizontalDiameter,
                                        canvasObject.getOutlineWidth()
                                    );
                                    previewElement.strokeWidth = strokeWidth;
                                }
                            }
                        } else if (preview.circularTextsPreview.length > 0) {
                            reject(
                                new Error(
                                    'Could not edit the text element with ID ' + (canvasObject.uuid || canvasObject.id)
                                )
                            );
                            return;
                        }
                    }
                    resolve();
                }
            });
        }

        /**
         * Add a placeholder image for the EPS
         * @param {Number}  centerX     Center X position of the text in the preview image in pixels
         * @param {Number}  centerY     Center Y position of the text in the preview image in pixels
         * @param {Number}  rotation    Text rotation in degrees
         * @param {Number}  skewX       Horizontal skew in degrees
         * @param {Number}  skewY       Vertical skew in degrees
         * @param {Number}  width       Text bounds width in pixels
         * @param {Number}  height      Text bounds height in pixels
         * @param {String}  color       Color string in the form of #rrggbb
         * @param {Boolean} fullColor   Defines if the image is full color (raster)
         * @param {Boolean} grading     Defines if color grading should be applied to the image
         * @param {Number}  zIndex      z-index of this element, 0 being the deepest level
         * @param {Number}  id          Unique id of this image placeholder
         * @param {Boolean} hasStaticImage      Whether or not the image is static (wont change)
         * @param {String}  imageUrl    image on Base64
         * @param {String}  maskPath    Path to the mask image if any, null otherwise
         * @param {String}  dynamicImagesPath    Path to the dynamic images if any, null otherwise
         * @param {Boolean}  removeWhiteBackground   Remove white background from images corners
         * @param {Boolean}  RemoveBackground        Remove  background from images with remove.bg
         * @param {Boolean}  isGuide        true if the image is a template product guide image
         * @param {Boolean}  allowEdit    The image can be edited in the preview
         * @param {Boolean}  allowSwapLibraryPosition    The image can swap library position in the preview
         */
        function addEPSPlaceholderImage(data) {
            const {
                centerX,
                centerY,
                rotation,
                skewX,
                skewY,
                width,
                height,
                color,
                fullColor,
                grading,
                RemoveBackground,
                opacity,
                zIndex,
                id,
                uuid,
                hasStaticImage,
                imageUrl,
                maskPath,
                dynamicImagesPath,
                imageLibraryId,
                colors,
                colorLibraryId,
                rotationStep,
                coverMaskArea,
                removeWhiteBackground,
                locked,
                visible,
                name,
                isPrintArea,
                isOverlay,
                isColorOptions,
                podProductImagesMetadata,
                groupId,
                mapConfig,
                wordCloudConfig,
                starMapConfig,
                wordSearchConfig,
                cartoonize,
                CutoutFace,
                removeBackgroundCOP,
                CustomilyRemoveBackground = false,
                CustomilyFaceCutOut = false,
                CustomilyImageFilter = 0,
                pageEpsId = null,
                flipX = false,
                flipY = false,
                isLimitImageToPlaceholder,
                textToImage,
                imageToImage = false,
                imageToImageConfig = null,
                imageEffects = null,
                isGuide,
                allowEdit,
                crossword,
                CrosswordConfig = null,
                calendar = false,
                calendarConfig = null,
                outlineWidth,
                outlineColor,
                testingUrl = '',
                isProductGuidelines,
                textMaskUuid,
                isMoonPhase = false,
                initCenterX,
                initCenterY,
                initWidth,
                initHeight,
                initRotation,
                allowSwapLibraryPosition = false
            } = data;

            let eps = getEps(pageEpsId);
            let image = {
                id: id,
                uuid: uuid,
                centerX: centerX,
                centerY: centerY,
                rotation: rotation,
                width: width,
                height: height,
                color: typeof color === 'object' ? JSON.stringify(color) : color,
                fullColor: fullColor,
                grading: grading,
                RemoveBackground: RemoveBackground,
                opacity: opacity,
                skewX: skewX,
                skewY: skewY,
                zIndex: zIndex,
                hasStaticImage: hasStaticImage,
                imageUrl: imageUrl,
                maskPath: maskPath,
                dynamicImagesPath: dynamicImagesPath,
                imageLibraryId: imageLibraryId,
                colors: colors,
                colorLibraryId: colorLibraryId,
                rotationStep: rotationStep,
                coverMaskArea: coverMaskArea,
                removeWhiteBackground: removeWhiteBackground,
                locked: locked,
                visible: visible,
                name: name,
                isPrintArea: isPrintArea,
                isOverlay: isOverlay,
                isColorOptions: isColorOptions,
                podProductImagesMetadata: podProductImagesMetadata,
                groupId: groupId,
                mapConfig,
                wordCloudConfig,
                starMapConfig,
                wordSearchConfig,
                cartoonize,
                CutoutFace,
                removeBackgroundCOP,
                CustomilyRemoveBackground,
                CustomilyFaceCutOut,
                CustomilyImageFilter,
                pageEpsId,
                flipX,
                flipY,
                isLimitImageToPlaceholder,
                textToImage,
                imageToImage,
                imageToImageConfig,
                imageEffects,
                isGuide,
                allowEdit,
                crossword,
                CrosswordConfig,
                calendar,
                calendarConfig,
                outlineWidth,
                outlineColor,
                testingUrl,
                isProductGuidelines,
                textMaskUuid,
                isMoonPhase,
                initCenterX,
                initCenterY,
                initWidth,
                initHeight,
                initRotation,
                allowSwapLibraryPosition
            };
            eps.imagePlaceHoldersEps.push(image);
        }

        /**
         * Update preview element with edited properties
         * @param {Object} preview Preview object to update
         * @param {Object} canvasObject Canvas object with edited properties
         * @returns {Object|null} Updated preview element or null if not found
         */
        function updatePreviewElement(preview, canvasObject) {
            let previewElement = null;
            if (!canvasObject.uuid) {
                let index = preview.imagePlaceHoldersPreview.findIndex(function (obj) {
                    return obj.id === canvasObject.id;
                });
                previewElement = preview.imagePlaceHoldersPreview[index];
            } else {
                previewElement = preview.imagePlaceHoldersPreview.find((i) => i.uuid === canvasObject.uuid);
            }

            if (previewElement) {
                // If the size changed, we have to recompute the size on the preview to keep the proportions the same
                if (canvasObject.editedSize) {
                    // Compute the element width and height
                    let eps = getEps(canvasObject.pageEpsId);
                    let scale = Math.min(preview.width / eps.width, preview.height / eps.height);
                    let boundingRectWidth = canvasObject.width * scale;
                    let boundingRectHeight = canvasObject.height * scale;

                    previewElement.width = boundingRectWidth;
                    previewElement.height = boundingRectHeight;
                }

                // Copy edited props to the preview element
                for (const prop of canvasObject.editedProps) {
                    if (prop === 'mask') {
                        previewElement['maskPath'] = canvasObject['maskPath'];
                    } else if (
                        previewGroupSync.syncTargetElementGroupEdit(previewElement, prop, canvasObject.groupId)
                    ) {
                        // TODO: ?
                    } else if (prop === 'initImagePosition') {
                        const scale = Math.min(
                            previewElement.width / canvasObject.width,
                            previewElement.height / canvasObject.height
                        );
                        previewElement.initCenterX = canvasObject.initCenterX ? canvasObject.initCenterX * scale : null;
                        previewElement.initCenterY = canvasObject.initCenterY ? canvasObject.initCenterY * scale : null;
                        previewElement.initWidth = canvasObject.initWidth ? canvasObject.initWidth * scale : null;
                        previewElement.initHeight = canvasObject.initHeight ? canvasObject.initHeight * scale : null;
                        previewElement.initRotation = canvasObject.initRotation;
                    } else {
                        previewElement[prop] = canvasObject[prop];
                    }
                }
            }

            return previewElement;
        }

        /**
         * Add a placeholder image to the product from the given object
         * @param {ImagePlaceholder} canvasObject Object to add to the product
         * @returns {ImagePlaceholder | undefined} Image Placeholder added into preview if the given element was added in edit mode
         */
        function addEPSPlaceholderImageFromObject(canvasObject, skipPreviewElements = false) {
            return $q(async function (resolve, reject) {
                // Does the image has a mask? Upload it but don't upload empty masks (black images of 10x10 pixels generated by ImagePlaceholder) and don't
                //  upload a new mask if it hasn't been changed
                if (
                    canvasObject.maskChanged &&
                    canvasObject.hasMask &&
                    (await ImageUtilService.imageToBase64(canvasObject.originalMaskElement)) !==
                        'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAoAAAAKCAYAAACNMs+9AAAAGElEQVQoU2NkYGD4z0AEYBxViC+UqB88AKk6CgERnGWPAAAAAElFTkSuQmCC'
                ) {
                    // Upload the image, set the path and add the element to the product
                    uploadImageToS3('mask', canvasObject.originalMaskElement).then(function (response) {
                        canvasObject.maskPath = response.Path;
                        addPlaceholderImage(canvasObject);
                    });
                } else if (!canvasObject.hasMask) {
                    canvasObject.maskPath = null;
                    addPlaceholderImage(canvasObject);
                } else {
                    addPlaceholderImage(canvasObject);
                }

                function addPlaceholderImage(canvasObject) {
                    const normalizedEpsGroupId = resolveGroupIdForTypeWithCounterpart(
                        canvasObject.groupId,
                        'eps',
                        createGroupIndex(getGroups())
                    );

                    addEPSPlaceholderImage({
                        centerX: canvasObject.left,
                        centerY: canvasObject.top,
                        rotation: canvasObject.angle,
                        skewX: canvasObject.skewX,
                        skewY: canvasObject.skewY,
                        width: canvasObject.getWidth(),
                        height: canvasObject.getHeight(),
                        color: canvasObject.color,
                        fullColor: canvasObject.fullColor,
                        grading: canvasObject.grading,
                        RemoveBackground: canvasObject.RemoveBackground,
                        opacity: 1,
                        zIndex: canvasObject.zIndex,
                        id: canvasObject.id,
                        uuid: canvasObject.uuid,
                        hasStaticImage: canvasObject.hasStaticImage,
                        imageUrl: canvasObject.imageUrl,
                        maskPath: canvasObject.maskPath,
                        dynamicImagesPath: canvasObject.dynamicImagesPath,
                        imageLibraryId: canvasObject.imageLibraryId,
                        colors: canvasObject.colors,
                        colorLibraryId: canvasObject.colorLibraryId,
                        rotationStep: canvasObject.rotationStep,
                        coverMaskArea: canvasObject.coverMaskArea,
                        removeWhiteBackground: canvasObject.removeWhiteBackground,
                        locked: canvasObject.locked,
                        visible: canvasObject.visible,
                        name: canvasObject.name,
                        isPrintArea: canvasObject.isPrintArea,
                        isOverlay: canvasObject.isOverlay,
                        isColorOptions: canvasObject.isColorOptions,
                        podProductImagesMetadata: canvasObject.podProductImagesMetadata,
                        groupId: normalizedEpsGroupId,
                        mapConfig: canvasObject.mapConfig,
                        wordCloudConfig: canvasObject.wordCloudConfig,
                        starMapConfig: canvasObject.starMapConfig,
                        wordSearchConfig: canvasObject.wordSearchConfig,
                        cartoonize: canvasObject.cartoonize,
                        CutoutFace: canvasObject.CutoutFace,
                        removeBackgroundCOP: canvasObject.removeBackgroundCOP,
                        CustomilyRemoveBackground: canvasObject.CustomilyRemoveBackground,
                        CustomilyFaceCutOut: canvasObject.CustomilyFaceCutOut,
                        CustomilyImageFilter: canvasObject.CustomilyImageFilter,
                        pageEpsId: canvasObject.pageEpsId,
                        flipX: canvasObject.getFlip().flipX,
                        flipY: canvasObject.getFlip().flipY,
                        isLimitImageToPlaceholder: canvasObject.isLimitImageToPlaceholder,
                        textToImage: canvasObject.textToImage,
                        imageToImage: canvasObject.imageToImage,
                        imageToImageConfig: canvasObject.imageToImageConfig,
                        imageEffects: canvasObject.imageEffects,
                        isGuide: canvasObject.isGuide,
                        allowEdit: canvasObject.allowEdit,
                        crossword: canvasObject.crossword,
                        CrosswordConfig: canvasObject.CrosswordConfig,
                        calendar: canvasObject.calendar,
                        calendarConfig: canvasObject.calendarConfig,
                        outlineWidth: canvasObject.outlineWidth,
                        outlineColor: canvasObject.outlineColor,
                        testingUrl: canvasObject.testingUrl,
                        isProductGuidelines: canvasObject.isProductGuidelines,
                        textMaskUuid: canvasObject.textMaskUuid,
                        isMoonPhase: canvasObject.isMoonPhase,
                        initCenterX: canvasObject.initCenterX,
                        initCenterY: canvasObject.initCenterY,
                        initWidth: canvasObject.initWidth,
                        initHeight: canvasObject.initHeight,
                        initRotation: canvasObject.initRotation,
                        allowSwapLibraryPosition: canvasObject.allowSwapLibraryPosition
                    });
                    updateProductionElementId(canvasObject, canvasObject.id);

                    if (skipPreviewElements) {
                        resolve();
                        return;
                    }

                    // Placeholders added on edit mode
                    if (canvasObject.addedOnEdit) {
                        try {
                            const image = addPreviewPlaceholderImage(
                                {
                                    centerX: canvasObject.left,
                                    centerY: canvasObject.top,
                                    rotation: canvasObject.angle,
                                    skewX: canvasObject.skewX,
                                    skewY: canvasObject.skewY,
                                    width: canvasObject.width,
                                    height: canvasObject.height,
                                    color: canvasObject.color,
                                    fullColor: canvasObject.fullColor,
                                    grading: canvasObject.grading,
                                    opacity: 1,
                                    zIndex: canvasObject.zIndex,
                                    id: canvasObject.id,
                                    uuid: canvasObject.uuid,
                                    hasStaticImage: canvasObject.hasStaticImage,
                                    imageUrl: canvasObject.imageUrl,
                                    maskPath: canvasObject.maskPath,
                                    dynamicImagesPath: canvasObject.dynamicImagesPath,
                                    imageLibraryId: canvasObject.imageLibraryId,
                                    colors: canvasObject.colors,
                                    colorLibraryId: canvasObject.colorLibraryId,
                                    rotationStep: canvasObject.rotationStep,
                                    coverMaskArea: canvasObject.coverMaskArea,
                                    removeWhiteBackground: canvasObject.removeWhiteBackground,
                                    RemoveBackground: canvasObject.RemoveBackground,
                                    addedOnEdit: true,
                                    locked: canvasObject.locked,
                                    visible: canvasObject.visible,
                                    name: canvasObject.name,
                                    isPrintArea: canvasObject.isPrintArea,
                                    isOverlay: canvasObject.isOverlay,
                                    isColorOptions: canvasObject.isColorOptions,
                                    podProductImagesMetadata: canvasObject.podProductImagesMetadata,
                                    groupId: previewGroupSync.resolveTargetGroupId(canvasObject.groupId),
                                    mapConfig: canvasObject.mapConfig,
                                    wordCloudConfig: canvasObject.wordCloudConfig,
                                    starMapConfig: canvasObject.starMapConfig,
                                    wordSearchConfig: canvasObject.wordSearchConfig,
                                    cartoonize: canvasObject.cartoonize,
                                    CutoutFace: canvasObject.CutoutFace,
                                    removeBackgroundCOP: canvasObject.removeBackgroundCOP,
                                    CustomilyRemoveBackground: canvasObject.CustomilyRemoveBackground,
                                    CustomilyFaceCutOut: canvasObject.CustomilyFaceCutOut,
                                    CustomilyImageFilter: canvasObject.CustomilyImageFilter,
                                    pageEpsId: canvasObject.pageEpsId,
                                    flipX: canvasObject.getFlip()?.flipX || false,
                                    flipY: canvasObject.getFlip()?.flipY || false,
                                    isLimitImageToPlaceholder: canvasObject.isLimitImageToPlaceholder,
                                    textToImage: canvasObject.textToImage,
                                    imageToImage: canvasObject.imageToImage,
                                    imageToImageConfig: canvasObject.imageToImageConfig,
                                    imageEffects: canvasObject.imageEffects,
                                    isGuide: canvasObject.isGuide,
                                    allowEdit: canvasObject.allowEdit,
                                    crossword: canvasObject.crossword,
                                    CrosswordConfig: canvasObject.CrosswordConfig,
                                    calendar: canvasObject.calendar,
                                    calendarConfig: canvasObject.calendarConfig,
                                    enablePerspective: false, // no canvasObject.enablePerspective for step1
                                    perspectiveCoords: null, // no canvasObject.perspectiveCoords for step1
                                    outlineWidth: canvasObject.outlineWidth,
                                    outlineColor: canvasObject.outlineColor,
                                    testingUrl: canvasObject.testingUrl,
                                    isProductGuidelines: canvasObject.isProductGuidelines,
                                    textMaskUuid: canvasObject.textMaskUuid,
                                    isMoonPhase: canvasObject.isMoonPhase,
                                    initCenterX: canvasObject.initCenterX,
                                    initCenterY: canvasObject.initCenterY,
                                    initWidth: canvasObject.initWidth,
                                    initHeight: canvasObject.initHeight,
                                    initRotation: canvasObject.initRotation,
                                    allowSwapLibraryPosition: canvasObject.allowSwapLibraryPosition
                                },
                                true
                            );

                            resolve({
                                previewElement: image,
                                newlyAdded: true
                            });
                        } catch (error) {
                            reject(error);
                        }
                    }
                    // If the object was edited, update the parameters in the preview so it shows updated in step2
                    else {
                        const selectedPreview = getPreview();
                        const listPreview = getListPreview();
                        let selectedPreviewElement = null;

                        // Process selectedPreview first
                        if (selectedPreview && (canvasObject.isEdited() || canvasObject.editedSize)) {
                            selectedPreviewElement = updatePreviewElement(selectedPreview, canvasObject);

                            if (!selectedPreviewElement && selectedPreview.imagePlaceHoldersPreview.length > 0) {
                                reject(
                                    new Error(
                                        'Could not edit the image element with ID ' +
                                            (canvasObject.uuid || canvasObject.id)
                                    )
                                );
                                return;
                            }
                        }

                        // Process remaining previews in listPreview (excluding selectedPreview)
                        for (const preview of listPreview) {
                            // Skip if this preview is the same as selectedPreview
                            if (preview === selectedPreview) {
                                continue;
                            }

                            if (canvasObject.isEdited() || canvasObject.editedSize) {
                                const previewElement = updatePreviewElement(preview, canvasObject);

                                if (!previewElement && preview.imagePlaceHoldersPreview.length > 0) {
                                    reject(
                                        new Error(
                                            'Could not edit the image element with ID ' +
                                                (canvasObject.uuid || canvasObject.id)
                                        )
                                    );
                                    return;
                                }
                            }
                        }

                        resolve({
                            previewElement: selectedPreviewElement,
                            newlyAdded: false
                        });
                    }
                }
            });
        }

        /**
         *
         * @param {Number}  centerX             Center X position of the vector EPS [pixels]
         * @param {Number}  centerY             Center Y position of the vector EPS [pixels]
         * @param {Number}  rotation            Rotation [degrees]
         * @param {Number}  skewX               Horizontal skew [degrees]
         * @param {Number}  skewY               Vertical skew [degrees]
         * @param {Number}  width               Width [pixels]
         * @param {Number}  height              Height [pixels]
         * @param {Number}  opacity             Opacity from 0 to 1
         * @param {Array}   vectors             Array of vectors for each position
         * @param colors
         * @param allowUserUpload               Wether to allow the user to upload an SVG element on the product preview
         * @param zIndex                        z-index of this element, 0 being the deepest level
         * @param id
         * @param uuid
         * @param colorLibraryId
         * @param vectorLibraryId
         * @param applyColoring
         * @param coverMaskArea
         * @param rotationStep
         * @param locked
         * @param visible
         * @param name
         * @param isPrintArea
         * @param isOverlay
         * @param groupId
         * @param starMapConfig
         * @param qrConfig
         * @param contentIsPdf
         * @param vectorContent
         * @param allowEdit
         * @param pageEpsId
         * @param flipX
         * @param flipY
         * @param isSolidBackground
         * @param {Boolean}  allowSwapLibraryPosition   The vector EPS can swap library position in the preview
         */
        function addEPSVectorEPS(
            centerX,
            centerY,
            rotation,
            skewX,
            skewY,
            width,
            height,
            opacity,
            vectors,
            colors,
            allowUserUpload,
            zIndex,
            id,
            uuid,
            colorLibraryId,
            vectorLibraryId,
            applyColoring,
            coverMaskArea,
            rotationStep,
            locked,
            visible,
            name,
            isPrintArea,
            isOverlay,
            groupId,
            starMapConfig,
            qrConfig,
            contentIsPdf,
            vectorContent,
            allowEdit,
            pageEpsId = null,
            flipX = false,
            flipY = false,
            isSolidBackground = false,
            allowSwapLibraryPosition = false
        ) {
            let eps = getEps(pageEpsId);
            let vectorEPSEps = {
                id: id,
                uuid: uuid,
                centerX: centerX,
                centerY: centerY,
                rotation: rotation,
                width: width,
                height: height,
                opacity: opacity,
                skewX: skewX,
                skewY: skewY,
                vectors: vectors,
                zIndex: zIndex,
                allowUserUpload: allowUserUpload,
                colors: colors,
                colorLibraryId: colorLibraryId,
                vectorLibraryId: vectorLibraryId,
                applyColoring: applyColoring,
                coverMaskArea: coverMaskArea,
                rotationStep: rotationStep,
                locked: locked,
                visible: visible,
                name: name,
                isPrintArea: isPrintArea,
                isOverlay: isOverlay,
                groupId: groupId,
                starMapConfig: starMapConfig,
                qrConfig: qrConfig,
                contentIsPdf: contentIsPdf,
                allowEdit: allowEdit,
                pageEpsId,
                flipX,
                flipY,
                isSolidBackground,
                allowSwapLibraryPosition: allowSwapLibraryPosition
            };
            if (contentIsPdf) {
                vectorEPSEps.pdf = vectorContent;
            } else {
                vectorEPSEps.svgString = vectorContent;
            }
            eps.vectorEPSEps.push(vectorEPSEps);
        }

        /**
         * Add a vector EPS to the product from the given object
         * @param {VectorPlaceholder} canvasObject Object to add to the product
         */
        function addEPSVectorEPSFromObject(canvasObject, skipPreviewElements = false) {
            return $q(function (resolve, reject) {
                addEPSVectorEPS(
                    canvasObject.left,
                    canvasObject.top,
                    canvasObject.angle,
                    canvasObject.skewX,
                    canvasObject.skewY,
                    canvasObject.width * canvasObject.scaleX,
                    canvasObject.height * canvasObject.scaleY,
                    1,
                    canvasObject.vectors,
                    canvasObject.colors,
                    canvasObject.allowUserUpload,
                    canvasObject.zIndex,
                    canvasObject.id,
                    canvasObject.uuid,
                    canvasObject.colorLibraryId,
                    canvasObject.vectorLibraryId,
                    canvasObject.applyColoring,
                    canvasObject.coverMaskArea,
                    canvasObject.rotationStep,
                    canvasObject.locked,
                    canvasObject.visible,
                    canvasObject.name,
                    canvasObject.isPrintArea,
                    canvasObject.isOverlay,
                    canvasObject.groupId,
                    canvasObject.starMapConfig,
                    canvasObject.qrConfig,
                    canvasObject.contentIsPdf,
                    canvasObject.contentIsPdf ? canvasObject.pdf : canvasObject.svgString,
                    canvasObject.allowEdit,
                    canvasObject.pageEpsId,
                    canvasObject.getFlip().flipX,
                    canvasObject.getFlip().flipY,
                    canvasObject.isSolidBackground,
                    canvasObject.allowSwapLibraryPosition
                );
                updateProductionElementId(canvasObject, canvasObject.id);

                if (skipPreviewElements) {
                    resolve();
                    return;
                }

                // Added vector svg on edit mode?
                if (canvasObject.addedOnEdit) {
                    const objectCenterX = canvasObject.left;
                    const objectCenterY = canvasObject.top;
                    const newWidth = canvasObject.width;
                    const newHeight = canvasObject.height;

                    addPreviewVectorEPS(
                        {
                            centerX: objectCenterX,
                            centerY: objectCenterY,
                            rotation: canvasObject.angle,
                            skewX: canvasObject.skewX,
                            skewY: canvasObject.skewY,
                            width: newWidth,
                            height: newHeight,
                            opacity: 1,
                            vectors: canvasObject.vectors,
                            colors: canvasObject.colors,
                            allowUserUpload: canvasObject.allowUserUpload,
                            zIndex: canvasObject.zIndex,
                            id: canvasObject.id,
                            uuid: canvasObject.uuid,
                            colorLibraryId: canvasObject.colorLibraryId,
                            vectorLibraryId: canvasObject.vectorLibraryId,
                            applyColoring: canvasObject.applyColoring,
                            coverMaskArea: canvasObject.coverMaskArea,
                            rotationStep: canvasObject.rotationStep,
                            addedOnEdit: true,
                            locked: canvasObject.locked,
                            visible: canvasObject.visible,
                            name: canvasObject.name,
                            isPrintArea: canvasObject.isPrintArea,
                            isOverlay: canvasObject.isOverlay,
                            groupId: previewGroupSync.resolveTargetGroupId(canvasObject.groupId),
                            starMapConfig: canvasObject.starMapConfig,
                            qrConfig: canvasObject.qrConfig,
                            contentIsPdf: canvasObject.contentIsPdf,
                            vectorContent: canvasObject.contentIsPdf ? canvasObject.pdf : canvasObject.svgString,
                            allowEdit: canvasObject.allowEdit,
                            pageEpsId: canvasObject.pageEpsId,
                            flipX: canvasObject.getFlip()?.flipX || false,
                            flipY: canvasObject.getFlip()?.flipY || false,
                            isLimitImageToPlaceholder: canvasObject.isLimitImageToPlaceholder,
                            clipPath: null,
                            isSolidBackground: canvasObject.isSolidBackground,
                            allowSwapLibraryPosition: canvasObject.allowSwapLibraryPosition
                        },
                        true
                    );
                    resolve();
                } else {
                    const listPreview = getListPreview();
                    // If the object was edited, update the parameters in the preview so it shows updated in step2
                    if (canvasObject.isEdited() || canvasObject.editedSize) {
                        for (const preview of listPreview) {
                            let previewElement = null;
                            if (!canvasObject.uuid) {
                                // Find the index of the element
                                let index = preview.vectorEPSPreview.findIndex(function (obj) {
                                    return obj.id === canvasObject.id;
                                });
                                previewElement = preview.vectorEPSPreview[index];
                            } else {
                                previewElement = preview.vectorEPSPreview.find((v) => v.uuid === canvasObject.uuid);
                            }

                            if (previewElement) {
                                // If the size changed, we have to recompute the size on the preview to keep the proportions the same
                                if (canvasObject.editedSize) {
                                    // Compute the element widgth and height
                                    let eps = getEps(canvasObject.pageEpsId);
                                    let scale = Math.min(preview.width / eps.width, preview.height / eps.height);
                                    let boundingRectWidth = canvasObject.width * scale;
                                    let boundingRectHeight = canvasObject.height * scale;

                                    previewElement.width = boundingRectWidth;
                                    previewElement.height = boundingRectHeight;
                                }

                                // Copy edited props to the preview element
                                for (const prop of canvasObject.editedProps) {
                                    if (
                                        previewGroupSync.syncTargetElementGroupEdit(
                                            previewElement,
                                            prop,
                                            canvasObject.groupId
                                        )
                                    ) {
                                        continue;
                                    }

                                    previewElement[prop] = canvasObject[prop];
                                }

                                // Copy vector content
                                if (canvasObject.contentIsPdf) {
                                    previewElement.pdf = canvasObject.pdf;
                                } else {
                                    if (canvasObject.svgString) {
                                        previewElement.svgString = canvasObject.svgString;
                                    }
                                }
                            }
                        }
                    }
                    resolve();
                }
            });
        }

        function setEPSBaseImagePath(path, epsId = null) {
            let eps = getEps(epsId);
            eps.baseImage = path;
        }

        function setEPSBaseEPSPath(path, epsId = null) {
            let eps = getEps(epsId);
            eps.baseEps = path;
        }

        function getProduct() {
            return product;
        }

        function getProductsCount(searchString) {
            ProductAPIService.getProductsCount(searchString);
        }

        function saveProduct(product) {
            return ProductAPIService.saveProduct(product);
        }

        function haveOldImgBehaviour() {
            return product?.listEPS
                ?.map((eps) =>
                    eps.imagePlaceHoldersEps?.some(
                        (ph) =>
                            ph.RemoveBackground ||
                            ph.CustomilyImageFilter > 0 ||
                            ph.CutoutFace ||
                            ph.removeBackgroundCOP ||
                            ph.CustomilyRemoveBackground ||
                            ph.CustomilyFaceCutOut
                    )
                )
                .some((a) => a);
        }

        /**
         * Update the id of the given element and also change the id of the associated element
         *  in the preview if any.
         * @param {Object} element Canvas element to update.
         * @param {Number} newId New id for the element.
         */
        function updateProductionElementId(element, newId) {
            element.id = newId;
            let uuid = element.uuid;

            if (uuid) {
                const listPreview = getListPreview();
                for (const preview of listPreview) {
                    let elements = preview.textsPreview
                        .concat(preview.circularTextsPreview)
                        .concat(preview.imagePlaceHoldersPreview)
                        .concat(preview.vectorEPSPreview);
                    let previewElement = elements.find((e) => e.uuid === uuid);

                    if (previewElement) {
                        previewElement.id = newId;
                    }
                }
            }
        }

        function getLastPreviewId() {
            const preview = getPreview();
            let maxTextId = 0;
            let maxCircularTextId = 0;
            let maxImageId = 0;
            let maxVectorId = 0;
            let sortFunction = function (a, b) {
                return b.id - a.id;
            };

            //sorts all list in reverse order and return the largest id for each list
            if (preview.textsPreview.length > 0) {
                maxTextId = preview.textsPreview.sort(sortFunction)[0].id;
            }
            if (preview.circularTextsPreview.length > 0) {
                maxCircularTextId = preview.circularTextsPreview.sort(sortFunction)[0].id;
            }
            if (preview.imagePlaceHoldersPreview.length > 0) {
                maxImageId = preview.imagePlaceHoldersPreview.sort(sortFunction)[0].id;
            }
            if (preview.vectorEPSPreview.length > 0) {
                maxVectorId = preview.vectorEPSPreview.sort(sortFunction)[0].id;
            }

            return Math.max(maxTextId, maxCircularTextId, maxImageId, maxVectorId);
        }

        function getAllIndexes(arr, val) {
            let indexes = [],
                i;
            for (i = 0; i < arr.length; i++) if (arr[i].id === val) indexes.push(i);

            return indexes;
        }

        /**
         * Get the EPS element corresponding to the preview element with the given id
         * @param {Number} id id of the element in the preview
         */
        function getEpsElementFromPreviewElement(targetElement) {
            let eps = getEps(targetElement.pageEpsId);

            let element = eps.textsEps.find((e) =>
                targetElement.uuid ? e.uuid === targetElement.uuid : e.id === targetElement.id
            );
            if (element) return element;

            element = eps.circularTextsEps.find((e) =>
                targetElement.uuid ? e.uuid === targetElement.uuid : e.id === targetElement.id
            );
            if (element) return element;

            element = eps.imagePlaceHoldersEps.find((e) =>
                targetElement.uuid ? e.uuid === targetElement.uuid : e.id === targetElement.id
            );
            if (element) return element;

            element = eps.vectorEPSEps.find((e) =>
                targetElement.uuid ? e.uuid === targetElement.uuid : e.id === targetElement.id
            );
            if (element) return element;

            return element;
        }

        function isPreviewOnlyElement(element, epsId = null) {
            if (!element) return false;
            let eps = getEps(element.pageEpsId || epsId);

            if (!eps) {
                return false;
            }

            if (element && element.dynamicImagesPath) {
                const placeholder = [...eps.imagePlaceHoldersEps, ...eps.vectorEPSEps];

                const isStep1DynamicImage = element.uuid
                    ? placeholder.some((i) => i.uuid === element.uuid)
                    : placeholder.some((i) => i.id == element.id);

                return !isStep1DynamicImage;
            }

            return false;
        }

        function getPreviewOnlyElements(elements, epsId = null) {
            if (!elements.length) return [];

            const epsElements = new Map();
            const eps = epsId ? getEps(epsId) : null;

            if (eps) {
                const placeholderIds = new Set(
                    [...eps.imagePlaceHoldersEps, ...eps.vectorEPSEps].map((e) => e.uuid || e.id)
                );
                epsElements.set(epsId, placeholderIds);
            }

            const results = [];
            for (const element of elements) {
                const elementEpsId = element.pageEpsId || epsId;
                let placeholders = epsElements.get(elementEpsId);

                if (!placeholders) {
                    const elementsEps = getEps(elementEpsId);

                    const placeholderIds = new Set(
                        [...elementsEps.imagePlaceHoldersEps, ...elementsEps.vectorEPSEps].map((e) => e.uuid || e.id)
                    );
                    epsElements.set(elementEpsId, placeholderIds);
                    placeholders = placeholderIds;
                }

                if (!placeholders.size || !placeholders.has(element.uuid || element.id)) {
                    results.push(element);
                }
            }

            return results;
        }

        let _selectedPreview = null;

        function setListPreview(listPreviews) {
            // Sort listPreviews by zIndex from lowest to highest
            if (listPreviews && Array.isArray(listPreviews)) {
                listPreviews.sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0));
            }
            product.listPreviews = listPreviews;
        }

        /**
         * Gets the preview by id.
         * If the id is null or not found, returns the first preview in the list or the product preview.
         * @returns {Object|null} The preview object or null if not found
         */
        function getPreview() {
            const selectedPreview = _selectedPreview;
            // Early return if no product is available
            if (!product) {
                return null;
            }

            if (selectedPreview && product.listPreviews?.length) {
                const foundPreview = product.listPreviews.find(
                    (preview) => preview.PreviewId == selectedPreview.PreviewId
                );
                if (foundPreview) {
                    return foundPreview;
                }
            }

            // Return the first preview from the list, or fallback to product.preview
            return product.listPreviews?.[0] || product.preview || null;
        }

        function getListPreview() {
            if (product.listPreviews?.length) {
                // Sort listPreviews by zIndex from lowest to highest
                const sortedPreviews = [...product.listPreviews].sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0));
                return sortedPreviews;
            }
            return [product.preview];
        }

        function setSelectedPreview(preview) {
            if (preview.PreviewId == _selectedPreview?.PreviewId) return;
            _selectedPreview = preview;
        }

        return {
            loadProduct: loadProduct,

            addGroups: addGroups,
            replaceGroupsByType: replaceGroupsByType,
            getGroups: getGroups,
            updatePreviewGroups: updatePreviewGroups,

            clearEPS: clearEPS,
            clearPreview: clearPreview,
            clearPreviewElements: clearPreviewElements,
            clearProduct: clearProduct,
            setProductId: setProductId,
            getProductId: getProductId,
            setProductName: setProductName,
            setMelcoReferenceId: setMelcoReferenceId,
            setProductFormat: setProductFormat,
            setProductColorMode: setProductColorMode,
            setMergeFiles: setMergeFiles,
            setPreviewImage: setPreviewImage,
            setPreviewBgColor: setPreviewBgColor,
            setPreviewImgSize: setPreviewImgSize,
            addPreviewText: addPreviewText,
            addPreviewPlaceholderImage: addPreviewPlaceholderImage,
            setEPSSize: setEPSSize,
            setEPSdpi: setEPSdpi,
            setExportPPI: setExportPPI,
            setEPSMirroring: setEPSMirroring,
            setEpsMergeOutput: setEpsMergeOutput,
            setNeedColorsUpgrade: setNeedColorsUpgrade,
            getNeedColorsUpgrade: getNeedColorsUpgrade,

            addPreviewCircularText: addPreviewCircularText,

            addEPSText: addEPSText,
            addEPSTextFromObject: addEPSTextFromObject,
            addEPSCircularTextFromObject: addEPSCircularTextFromObject,

            addEPSPlaceholderImage: addEPSPlaceholderImage,
            addEPSPlaceholderImageFromObject: addEPSPlaceholderImageFromObject,

            addEPSVectorEPS: addEPSVectorEPS,
            addEPSVectorEPSFromObject: addEPSVectorEPSFromObject,
            addPreviewVectorEPS: addPreviewVectorEPS,

            setEPSBaseImagePath: setEPSBaseImagePath,
            setEPSBaseEPSPath: setEPSBaseEPSPath,
            getProduct: getProduct,
            getProductsCount: getProductsCount,
            getPreviewImage: getPreviewImage,
            getPreviewBgColor: getPreviewBgColor,
            saveProduct: saveProduct,
            haveOldImgBehaviour: haveOldImgBehaviour,
            removeFromPreview: removeFromPreview,
            recoverFromPreview: recoverFromPreview,
            clearObjectsDeleteOnEPS: clearObjectsDeleteOnEPS,
            getLastPreviewId: getLastPreviewId,
            getEpsElementFromPreviewElement: getEpsElementFromPreviewElement,
            updateProductionElementId: updateProductionElementId,
            isPreviewOnlyElement: isPreviewOnlyElement,

            setProductTemplatePreviewTargetSize: setProductTemplatePreviewTargetSize,
            getProductTemplatePreviewTargetSize: getProductTemplatePreviewTargetSize,
            setPodAssignments: setPodAssignments,
            getPodAssignments: getPodAssignments,
            setPod: setPod,
            getPod: getPod,
            setDescription: setDescription,
            getDescription: getDescription,
            getProductColorsFilenames: getProductColorsFilenames,
            setProductColorsFilenames: setProductColorsFilenames,
            setTemplateProductVariants: setTemplateProductVariants,
            getTemplateProductVariants: getTemplateProductVariants,
            clearChildTemplateProduct: clearChildTemplateProduct,
            setChildTemplateProduct: setChildTemplateProduct,
            getChildTemplateProduct: getChildTemplateProduct,
            setPreviewColorOptionsMetadata: setPreviewColorOptionsMetadata,
            getPreviewColorOptionsMetadata: getPreviewColorOptionsMetadata,
            setIsProductColorModified: setIsProductColorModified,
            getIsProductColorModified: getIsProductColorModified,
            getStarMapMutex: getStarMapMutex,
            getQrMutex: getQrMutex,
            setEditMode: setEditMode,
            getEditMode: getEditMode,
            getEps: getEps,
            getElementsIdAndTestingValues: getElementsIdAndTestingValues,
            setTestingValues: setTestingValues,
            setImagesTestingValues: setImagesTestingValues,
            addNewEPS: addNewEPS,
            addListEPS: addListEPS,
            getListEPS: getListEPS,
            updateEPSFromPreview: updateEPSFromPreview,
            deleteEPS: deleteEPS,
            setProductSnapshotUrl: setProductSnapshotUrl,
            getProductSnapshotUrl: getProductSnapshotUrl,
            setProductThumbnail: setProductThumbnail,
            getProductThumbnail: getProductThumbnail,
            setListPreview,
            getPreview,
            getListPreview,
            setSelectedPreview,
            _selectedPreview,
            getPreviewOnlyElements,
            getElementsLinks,
            setElementsLinks,
            getProductWillUpdateOptionSets,
            setProductWillUpdateOptionSets
        };
    }
})();
